import React, { useLayoutEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '../../lib/utils';
import { ChevronIcon } from './icons';

/** Option font sizes from boards 18 and 20: keys/strings 15, default 14, structure 13. */
const SIZE_CLASS = { md: 'text-[15px]', sm: 'text-[14px]', xs: 'text-[13px]' };

const OPTION_BASE =
  'inline-flex h-11 min-w-0 items-center justify-center rounded-[6px] p-0 leading-none ' +
  'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
  'disabled:cursor-not-allowed enabled:hover:bg-tile/50';

const RAISED = 'bg-tile shadow-[0_1px_2px_rgba(37,34,29,0.12)] enabled:hover:bg-tile';

/** The spring the Major/Minor switch on `main` used (TonalitySwitch), so `slide` feels the same. */
const SLIDE_SPRING = { type: 'spring', stiffness: 700, damping: 30 };

function normalize(option) {
  return typeof option === 'string' ? { value: option, label: option } : option;
}

/**
 * Which edges of a horizontally scrolling element still have content past them.
 * `start` is true once scrolled away from the left edge, `end` while more is hidden on the
 * right. A 1px slack absorbs the sub-pixel rounding of scrollLeft on zoomed screens.
 */
export function scrollEdges(el) {
  if (!el) return { start: false, end: false };
  return {
    start: el.scrollLeft > 1,
    end: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
  };
}

const FADE_BASE =
  'pointer-events-none absolute inset-y-0 flex w-10 items-center text-ink-muted transition-opacity duration-150';
// Fades run from the tray colour to transparent. They use the `tray` token so dark mode follows.
const FADE_END_STYLE = { background: 'linear-gradient(to right, hsl(var(--tray) / 0), hsl(var(--tray)))' };
const FADE_START_STYLE = { background: 'linear-gradient(to left, hsl(var(--tray) / 0), hsl(var(--tray)))' };

/** How far the swipe row peeks when it first appears (px), and how long it holds before settling back (ms). */
const PEEK_DISTANCE = 40;
const PEEK_HOLD_MS = 450;

/**
 * Grouped options in a sand container; the selected option is a raised paper tile.
 * Source: boards 18 and 20 (tray radius 8, padding 3, 2px gap). Options are 44px tall, the touch-target minimum.
 *
 * Single-select (default): role="radiogroup" with roving tabindex. Only the selected
 * option is in the tab order. Arrow Left/Right/Up/Down move the selection by one
 * (wrapping, skipping disabled options; in a grid, Up/Down step by one like
 * Left/Right), Home/End jump to the first/last enabled option.
 *
 * Multi-select (`multiple`): `value` is an array. Each option is an aria-pressed
 * toggle button inside role="group", and every option is tabbable.
 *
 * @param {object} props
 * @param {Array<string | {value: string, label: React.ReactNode, ariaLabel?: string, disabled?: boolean, span?: number}>} props.options
 *   `span` makes an option cover that many grid columns (grid mode only), e.g. a 6-column grid of
 *   3 + 2 options where the second row's options are wider.
 * @param {string | string[]} props.value Selected value, or array of selected values when `multiple`.
 * @param {(value: string) => void} props.onChange Called with the clicked/arrowed-to option's value.
 *   In multi mode it is called with the toggled option's value, not the new array.
 * @param {string} props.ariaLabel Names the group for assistive tech (required).
 * @param {boolean} [props.multiple] Toggle mode, `value` is an array.
 * @param {number} [props.columns] Grid column count; defaults to one column per option.
 * @param {boolean} [props.scroll] Single horizontally scrollable row instead of a grid (phone swipe row;
 *   the scrollbar is hidden, as on the board, so use it where touch scrolling is expected).
 *   A soft fade on the right edge says there is more to swipe to; it goes away at the end of
 *   the row, and a matching fade appears on the left once scrolled. Each fade carries a chevron
 *   pointing at the hidden options. When the row first appears with options off screen it peeks:
 *   slides PEEK_DISTANCE left and settles back, so the motion itself says "swipe" (skipped under
 *   prefers-reduced-motion). The fades ignore pointer events.
 * @param {'md'|'sm'|'xs'} [props.size] Option font size: md 15px, sm 14px (default), xs 13px.
 * @param {boolean} [props.slide] Single-select, one row only: the raised tile is one element that
 *   springs side to side to the selected option (like a toggle switch) instead of jumping. Looks the
 *   same at rest. Used for Major/Minor. Honours prefers-reduced-motion.
 * @param {boolean} [props.disabled] Greys out and disables the whole tray.
 * @param {string} [props.className] Extra classes for the tray container.
 * @param {string} [props.optionClassName] Extra classes for every option (e.g. a font tweak).
 */
export function Tray({
  options,
  value,
  onChange,
  ariaLabel,
  multiple = false,
  columns,
  scroll = false,
  slide = false,
  size = 'sm',
  disabled = false,
  className,
  optionClassName,
}) {
  const items = options.map(normalize);
  const itemRefs = useRef([]);
  const scrollerRef = useRef(null);
  const [edges, setEdges] = useState({ start: false, end: false });
  const reduceMotion = useReducedMotion();
  // The sliding tile only makes sense for a single-select grid on one row.
  const sliding = slide && !multiple && !scroll;
  const selectedIndex = sliding ? items.findIndex((item) => item.value === value) : -1;

  // Scroll mode: track which edges have hidden content. Runs after every render (cheap, and
  // setState bails out when nothing changed) so a new option list is measured, and on resize.
  useLayoutEffect(() => {
    if (!scroll) return undefined;
    const update = () => {
      const next = scrollEdges(scrollerRef.current);
      setEdges((prev) => (prev.start === next.start && prev.end === next.end ? prev : next));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  });

  // Scroll mode: peek once on mount if something is hidden past the right edge.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!scroll || reduceMotion || !el || typeof el.scrollTo !== 'function') return undefined;
    if (!scrollEdges(el).end) return undefined;
    el.scrollTo({ left: PEEK_DISTANCE, behavior: 'smooth' });
    const timer = setTimeout(() => {
      // Only settle back if the player hasn't taken over the scroll meanwhile.
      if (el.scrollLeft <= PEEK_DISTANCE + 1) el.scrollTo({ left: 0, behavior: 'smooth' });
    }, PEEK_HOLD_MS);
    return () => clearTimeout(timer);
    // Mount only: a peek on every render would fight the player's own swipes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSelected = (v) => (multiple ? value.includes(v) : value === v);
  const isDisabled = (item) => disabled || Boolean(item.disabled);

  // Roving tabindex target: the selected option, else the first enabled one.
  let tabbable = items.findIndex((item) => item.value === value && !isDisabled(item));
  if (tabbable < 0) tabbable = items.findIndex((item) => !isDisabled(item));

  function moveTo(index) {
    onChange(items[index].value);
    const el = itemRefs.current[index];
    if (el) el.focus();
  }

  function handleKeyDown(event, index) {
    if (multiple) return;
    const last = items.length - 1;
    let step = 0;
    let target = -1;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') step = 1;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') step = -1;
    else if (event.key === 'Home') target = items.findIndex((item) => !isDisabled(item));
    else if (event.key === 'End') {
      for (let i = last; i >= 0 && target < 0; i--) if (!isDisabled(items[i])) target = i;
    } else return;

    event.preventDefault();
    if (step !== 0) {
      for (let n = 1; n <= items.length; n++) {
        const i = (index + step * n + items.length * n) % items.length;
        if (!isDisabled(items[i])) {
          target = i;
          break;
        }
      }
    }
    if (target >= 0 && target !== index) moveTo(target);
  }

  const buttons = items.map((item, index) => {
    const selected = isSelected(item.value);
    const itemDisabled = isDisabled(item);
    const stateClasses = multiple
      ? selected
        ? cn(RAISED, 'font-extrabold text-ink')
        : 'font-extrabold text-ink-muted line-through'
      : selected
        ? cn(sliding ? 'relative' : RAISED, 'font-extrabold text-root-text')
        : cn(sliding && 'relative', 'font-semibold text-ink/85');

    return (
      <button
        key={item.value}
        ref={(el) => {
          itemRefs.current[index] = el;
        }}
        type="button"
        {...(multiple
          ? { 'aria-pressed': selected }
          : { role: 'radio', 'aria-checked': selected, tabIndex: index === tabbable ? 0 : -1 })}
        aria-label={item.ariaLabel}
        disabled={itemDisabled}
        onClick={() => onChange(item.value)}
        onKeyDown={(event) => handleKeyDown(event, index)}
        className={cn(
          OPTION_BASE,
          SIZE_CLASS[size],
          scroll ? 'min-w-11 whitespace-nowrap px-3' : 'w-full',
          stateClasses,
          // A disabled tray already dims as a whole; only dim single disabled options here.
          item.disabled && !disabled && 'opacity-40',
          optionClassName,
        )}
        style={!scroll && item.span ? { gridColumn: `span ${item.span} / span ${item.span}` } : undefined}
      >
        {item.label}
      </button>
    );
  });

  const groupProps = {
    role: multiple ? 'group' : 'radiogroup',
    'aria-label': ariaLabel,
    'aria-disabled': disabled || undefined,
  };

  if (scroll) {
    return (
      <div {...groupProps} className={cn('relative rounded-tray bg-tray', disabled && 'opacity-40', className)}>
        <div
          ref={scrollerRef}
          onScroll={() => setEdges(scrollEdges(scrollerRef.current))}
          className="overflow-x-auto rounded-tray [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <div className="flex w-max gap-[2px] p-[3px]">{buttons}</div>
        </div>
        <span
          aria-hidden="true"
          data-fade="start"
          className={cn(FADE_BASE, 'left-0 justify-start pl-0.5 rounded-l-tray', edges.start ? 'opacity-100' : 'opacity-0')}
          style={FADE_START_STYLE}
        >
          <ChevronIcon direction="left" size={16} />
        </span>
        <span
          aria-hidden="true"
          data-fade="end"
          className={cn(FADE_BASE, 'right-0 justify-end pr-0.5 rounded-r-tray', edges.end ? 'opacity-100' : 'opacity-0')}
          style={FADE_END_STYLE}
        >
          <ChevronIcon direction="right" size={16} />
        </span>
      </div>
    );
  }

  const count = columns || items.length;
  return (
    <div
      {...groupProps}
      className={cn('grid gap-[2px] rounded-tray bg-tray p-[3px]', sliding && 'relative', disabled && 'opacity-40', className)}
      style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}
    >
      {sliding && selectedIndex >= 0 && (
        // One tile for the whole tray, placed over the selected cell (3px padding, 2px gaps).
        // `layout` animates the jump in `left` as a spring; the option buttons sit above it.
        <motion.span
          aria-hidden="true"
          layout="position"
          transition={reduceMotion ? { duration: 0 } : SLIDE_SPRING}
          className={cn('pointer-events-none absolute inset-y-[3px] rounded-[6px]', RAISED)}
          style={{
            width: `calc((100% - 6px - ${(count - 1) * 2}px) / ${count})`,
            left: `calc(3px + ${selectedIndex} * ((100% - 6px - ${(count - 1) * 2}px) / ${count} + 2px))`,
          }}
        />
      )}
      {buttons}
    </div>
  );
}
